import type {KeyboardEvent, ReactNode} from 'react'
import {useCallback, useEffect, useRef, useState} from 'react'
import {ChevronDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, TextInput} from '@primer/react'

import styles from './ConversationHeader.module.css'

const MAX_TITLE_INPUT_LENGTH = 255

export interface ConversationHeaderTitleProps {
  title: string
  /** Portal container name forwarded to the title menu overlay. */
  portalContainerName?: string
  leadingVisual?: ReactNode
  /** ActionList content for the title dropdown; omit for a plain heading. */
  menuItems?: ReactNode
  /** Complete title-dropdown content for menus that include details before their action list. */
  menuContent?: ReactNode
  /** Called when pointer or keyboard interaction indicates that the title menu may open. */
  onMenuTriggerIntent?: () => void
  /** Accessible label for the dropdown trigger, combined with `title`. */
  menuAriaLabel?: string
  /** Heading level, or `span` when a host-provided heading already wraps this title. */
  titleAs?: 'h1' | 'h2' | 'h3' | 'span'
  /** Optional id for `aria-labelledby` wiring. */
  titleId?: string
  isEditingTitle?: boolean
  onTitleCommit?: (next: string) => void
  onTitleCancel?: () => void
  renamePending?: boolean
  titleInputLabel?: string
  titleContainerRef?: (element: HTMLElement | null) => void
}

export interface ConversationHeaderProps extends ConversationHeaderTitleProps {
  /** Presentational metadata between title and actions (e.g. "735 AI credits"), rendered muted; caller supplies the content. */
  metadata?: ReactNode
  actions?: ReactNode
}

export function ConversationHeaderTitle({
  title,
  portalContainerName,
  leadingVisual,
  menuItems,
  menuContent,
  onMenuTriggerIntent,
  menuAriaLabel = 'Conversation options',
  titleAs: TitleHeading = 'h1',
  titleId,
  isEditingTitle = false,
  onTitleCommit,
  onTitleCancel,
  renamePending = false,
  titleInputLabel = 'Rename conversation',
  titleContainerRef,
}: ConversationHeaderTitleProps) {
  const menuTriggerRef = useRef<HTMLButtonElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)

  const wasEditingTitleRef = useRef(isEditingTitle)
  useEffect(() => {
    const wasEditingTitle = wasEditingTitleRef.current
    wasEditingTitleRef.current = isEditingTitle
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- reacts to caller-owned `isEditingTitle`, not an in-component event
    if (!wasEditingTitle || isEditingTitle) return
    requestAnimationFrame(() => {
      if (document.activeElement !== document.body) return
      ;(menuTriggerRef.current ?? headingRef.current)?.focus()
    })
  }, [isEditingTitle])

  const [wasEditingTitleForSeed, setWasEditingTitleForSeed] = useState(isEditingTitle)
  const [draftTitle, setDraftTitle] = useState(title)
  const [seedTitle, setSeedTitle] = useState(title.trim())
  if (isEditingTitle !== wasEditingTitleForSeed) {
    setWasEditingTitleForSeed(isEditingTitle)
    if (isEditingTitle) {
      setDraftTitle(title)
      setSeedTitle(title.trim())
    }
  }

  const titleInputRef = useCallback((element: HTMLInputElement | null) => {
    if (!element) return
    element.focus()
    element.select()
  }, [])

  const handleSave = useCallback(() => {
    const trimmed = draftTitle.trim()
    if (trimmed && trimmed !== seedTitle) {
      onTitleCommit?.(trimmed)
    } else {
      onTitleCancel?.()
    }
  }, [draftTitle, seedTitle, onTitleCommit, onTitleCancel])

  const handleCancel = useCallback(() => {
    if (renamePending) return
    onTitleCancel?.()
  }, [onTitleCancel, renamePending])

  const handleTitleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.nativeEvent.isComposing) return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- input-level Enter-to-save, not an app shortcut
      if (event.key === 'Enter') {
        event.preventDefault()
        if (!renamePending) handleSave()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- input-level Escape-to-cancel, not an app shortcut
      } else if (event.key === 'Escape') {
        event.preventDefault()
        handleCancel()
      }
    },
    [handleSave, handleCancel, renamePending],
  )

  const handleEditActionsKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.nativeEvent.isComposing) return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Escape-to-cancel from the Save/Cancel buttons, not an app shortcut
      if (event.key === 'Escape') {
        event.preventDefault()
        handleCancel()
      }
    },
    [handleCancel],
  )

  const trimmedDraftTitle = draftTitle.trim()
  const isSaveDisabled = !trimmedDraftTitle || trimmedDraftTitle === seedTitle

  const titleText = <span className={styles.titleText}>{title}</span>
  const titleContent = isEditingTitle ? (
    title
  ) : menuItems != null || menuContent != null ? (
    <ActionMenu anchorRef={menuTriggerRef}>
      <ActionMenu.Anchor>
        <Button
          ref={menuTriggerRef}
          type="button"
          variant="invisible"
          trailingAction={ChevronDownIcon}
          className={styles.titleButton}
          aria-label={`${title}, ${menuAriaLabel}`}
          data-testid="conversation-header-menu-button"
          onPointerEnter={onMenuTriggerIntent}
          onPointerDown={onMenuTriggerIntent}
          onFocus={onMenuTriggerIntent}
        >
          {titleText}
        </Button>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay align="end" portalContainerName={portalContainerName}>
        {menuContent ?? <ActionList>{menuItems}</ActionList>}
      </ActionMenu.Overlay>
    </ActionMenu>
  ) : (
    titleText
  )
  const titleClassName = isEditingTitle ? 'sr-only' : styles.titleHeading

  const titleClusterContent = (
    <>
      {leadingVisual != null && <span className={styles.leadingVisual}>{leadingVisual}</span>}
      {TitleHeading === 'span' ? (
        <span id={titleId} className={titleClassName}>
          {titleContent}
        </span>
      ) : (
        <TitleHeading ref={headingRef} id={titleId} aria-label={title} tabIndex={-1} className={titleClassName}>
          {titleContent}
        </TitleHeading>
      )}
      {isEditingTitle && (
        <>
          <TextInput
            ref={titleInputRef}
            aria-label={titleInputLabel}
            value={draftTitle}
            onChange={event => setDraftTitle(event.target.value)}
            maxLength={MAX_TITLE_INPUT_LENGTH}
            className={styles.titleInput}
            data-conversation-title-input=""
            onKeyDown={handleTitleInputKeyDown}
            readOnly={renamePending}
          />
          <span className={styles.titleEditActions}>
            <Button
              type="button"
              size="small"
              onClick={handleCancel}
              onKeyDown={handleEditActionsKeyDown}
              inactive={renamePending || undefined}
              aria-disabled={renamePending || undefined}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="small"
              variant="primary"
              onClick={handleSave}
              onKeyDown={handleEditActionsKeyDown}
              disabled={isSaveDisabled && !renamePending}
              loading={renamePending}
              loadingAnnouncement="Saving the conversation name"
            >
              Save
            </Button>
          </span>
        </>
      )}
    </>
  )

  return TitleHeading === 'span' ? (
    <span ref={titleContainerRef} className={styles.titleCluster}>
      {titleClusterContent}
    </span>
  ) : (
    <div ref={titleContainerRef} className={styles.titleCluster}>
      {titleClusterContent}
    </div>
  )
}

export function ConversationHeader({metadata, actions, ...titleProps}: ConversationHeaderProps) {
  return (
    <div className={styles.container} data-testid="conversation-header">
      <ConversationHeaderTitle {...titleProps} />
      {(metadata != null || actions != null) && (
        <div className={styles.trailingCluster}>
          {metadata != null && (
            <div className={styles.metadata} data-testid="conversation-header-metadata">
              {metadata}
            </div>
          )}
          {actions != null && <div className={styles.actions}>{actions}</div>}
        </div>
      )}
    </div>
  )
}
